import {useCallback, useContext} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {addDays} from 'date-fns/addDays'
import {format} from 'date-fns/format'
import {usePricePrediction} from 'hooks/usePricePrediction'
import {useShowHotelDatePickerOverlay} from 'hooks/useShowOverlay/useShowOverlay'
import {getCurrencyCode} from 'modules/meta/selectors'
import {DateGuestPickerSource} from 'modules/overlay/types'
import {
  setOpenDatePickerType,
  setSearchBoxValues
} from 'modules/searchBox/slice'

import {Text} from '@vio/atlas/designSystem/Text'
import {Box} from '@vio/atlas/src/components/designSystem/helpers/Box'
import {useDeviceLayout} from '@vio/atlas/src/context/deviceLayout'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/hooks/useDispatchTeamEvent'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  Team
} from '@vio/core/src/analytics/types/Events'
import {DatePickerType, DatePickerVariant} from '@vio/core/src/datePicker/types'
import {ConfigureI18nContext} from '@vio/core/src/localization/components/ConfigureI18n'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {PricePrediction} from '@vio/shared/src/search/DatePicker/PricePrediction'

interface Props {
  variant: DatePickerVariant
  hotelId: string
}

export const ConditionalPricePrediction = ({hotelId, variant}: Props) => {
  const dispatch = useDispatch()
  const dispatchTeamEvent = useDispatchTeamEvent()
  const showHotelDatePickerOverlay = useShowHotelDatePickerOverlay()
  const currencyCode = useSelector(getCurrencyCode)
  const i18n = useContext(ConfigureI18nContext)
  const {isMobile} = useDeviceLayout()
  const {prices, nights, hasAvailablePrices} = usePricePrediction(hotelId)

  const handleDayClick = useCallback(
    (date: Date) => {
      const checkIn = format(date, 'yyyy-MM-dd')
      const checkOut = format(addDays(date, nights), 'yyyy-MM-dd')

      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: Entity.PriceInsights,
          action: Action.Clicked,
          payload: {checkIn, checkOut, component: 'PricePrediction'}
        })
      )

      dispatch(
        setSearchBoxValues({
          checkIn,
          checkOut
        })
      )

      if (isMobile) {
        showHotelDatePickerOverlay(
          DatePickerType.CheckIn,
          Team.Default,
          DateGuestPickerSource.AccommodationPageSearch
        )

        return
      }

      dispatch(setOpenDatePickerType(DatePickerType.CheckIn))
    },
    [dispatch, dispatchTeamEvent, showHotelDatePickerOverlay, isMobile, nights]
  )

  if (!hasAvailablePrices || !hotelId) return null

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        gap={variant === 'horizontal' ? 's600' : 's300'}
      >
        <Box
          paddingLeft={variant === 'vertical' ? 's500' : 's000'}
          paddingRight={variant === 'vertical' ? 's500' : 's000'}
        >
          <Text
            variant={variant === 'horizontal' ? 'titleL' : 'titleM'}
            tag="h3"
          >
            <FormattedMessageWrapper
              id="pricePrediction.title"
              defaultMessage="Prices for upcoming dates"
            />
          </Text>
        </Box>
        <PricePrediction
          variant={variant}
          prices={prices}
          currency={currencyCode}
          firstDayOfWeek={i18n.firstDayOfWeek}
          weekdaysShort={i18n.weekDayNamesShort}
          months={i18n.monthNames}
          onDayClick={handleDayClick}
        />
      </Box>
    </>
  )
}
